/**
 * Format specs for agent prompt responses. These are injected into prompts as
 * HTML comments (<!-- -->) so the model receives precise structure instructions
 * without displaying them in the UI.
 */

/** Format instruction for the medical history response. Inlined as a comment in the user prompt. */
export const getMedicalHistoryResponseFormat = (
  patientName: string,
  patientId: string,
) => `<!--
Generate a concise medical history record summary for patient **${patientName} — ${patientId}**.

## Workflow

### Step 1: Conversations and Messages Retrieval
Retrieve all messages associated with the **current patient** in the conversation that have an **OPEN** or **UNREAD** state using the conversation agent.

### Step 2: Medical History Records Retrieval
Transfer to the timeline agent and fetch the complete medical history records associated with the **current patient**.

**Attachment Processing**
- Identify and prioritize attachments of the following types:
  - **Previous Medical Records (PMR)** — from other veterinary facilities.
  - **Imaging/Radiology Results** — X-rays, ultrasounds, CT scans, MRI reports.
  - **Lab Test Results** — bloodwork, urinalysis, cytology, histopathology reports.
- Extract key medical information including:
  - **From PMR:** Historical diagnoses, treatments, medications, procedures, and prior test results.
  - **From Imaging:** Findings, impressions, radiologist interpretations, and recommendations.
  - **From Lab Results:** Specific test values, reference ranges, abnormal flags, and interpretations.
- Integrate attachment findings into the appropriate output sections (Active Problem, Diagnostic Indicators, Follow-ups, etc.).
- Preserve page numbers and source information for citations in the final output.

### Step 3: Follow-up Processing

**Step 3.1 — Identification**
Identify outstanding follow-up items from:
1. Appointment-associated SOAP medical notes and discharge notes retrieved in Step 2.
2. Messages retrieved in Step 1.

**Step 3.2 — Consolidation and Reconciliation**
- Consolidate overlapping or conflicting follow-up items across sources.
- **More recent information takes precedence.**

**Step 3.3 — Resolution Rule (Strict Suppression — Zero Tolerance)**
- If **any** source indicates a follow-up is completed, resolved, canceled, closed, no longer needed, or similar, that item **must be completely removed** from the follow-ups output.
- Treat it as if the item never existed.

**Step 3.4 — Exclusions**
Do not surface follow-up items that were **generated or suggested by AI**.

## Output Requirements

### Chat Readability
- Separate each main section (**## 1** through **## 7**) with a Markdown horizontal rule: '---' on its own line.
- Write for a chat UI: short lines and compact bullets.
- Use Markdown only (headings, bullets, bold, and tables only when necessary). No HTML.
- Have only one level of bullet points with title followed by 1–2 concise sentences of clinical detail (without additional sub-bullet points).
- Avoid long paragraphs: maximum 3 lines per paragraph. Highlight words thats of high importance depending on context.
- Prefer lists over prose. Each bullet should be 1 sentence (maximum 25 words) unless a section requires more detail.
- If a required section has no data, include the header and write 'None.' as a single plain line (not a bullet). If the section has a description line, place 'None.' on the next line after the description.
- Do not include internal reasoning, analysis, or meta commentary. Output final formatted content only.

### Inline Citations
- Use '_links' returned by timeline entities as citation targets.
- Render citations as Markdown links only: '[<Title>](<href>)'. Do not show raw URLs.
- Place citations inline at the end of the sentence they support, without surrounding parentheses.
  - Example: '... [View Soap](<soap_self_href>).'
- Use at most **one** citation link per bullet.
- Reuse the same citation link when multiple bullets in a section rely on the same source.
- Do not add a separate "Sources" or "Citations" section.

**Link selection rules**
- SOAP-derived information: use SOAP '_links.self' (title: 'View Soap').
- Lab-test-derived information: use LabTestLog '_links.self' (title: 'View Lab Test').
- Attachment-derived information: use Attachment '_links.self' (title: 'View Attachment').
- Timeline access: use Timeline '_links.self' (title: 'View Patient Timeline').
- If no correct link exists for a statement, omit the citation rather than inventing one.

### Normalization
- Deduplicate: merge duplicates across sources (SOAP, discharge, messages, attachments) and keep the most recent details.
- Terminology: use 'Unknown' instead of guessing.
- Sorting:
  - Active Problem: most recently created/updated first.
  - Diagnostic Indicators: newest order date first.
  - Current Medications: newest prescribed first.

**CRITICAL:** Strictly adhere to the output structure below.
- The very first line of the response must be a top-level Markdown title:
  - '# Medical History Summary — <Patient Name>'
- Do not include sections such as "Appointment" or "Summary".

---
## Title

---

## 1. Signalment
- **Patient:** <name>
- **Client:** <Firstname Lastname>.
- **Key demographics:** species, breed, date of birth, age, current weight and measured date, active/deceased status.
- Timeline link to complete patient timeline in Rhapsody PIMS using Timeline '_links.self'.

---

## 2. Active Problem
- List problem logs and diagnoses that are **not** in a resolved or removed state.
- Include status and creation date.
- From SOAP medical notes, extract relevant diagnostics, treatment plan, and monitoring plan when applicable.
- Group recurring problems together.

---

## 3. Diagnostic Indicators
- Add description: 'Diagnostic testing ordered during the last 12 months:'
- List lab tests and imaging entities within 12 months from today.
- Present results as bullets grouped by appointment (use the appointment's date/time and name as the group header).
  - If a test is not clearly associated with a specific appointment, group it under: 'Other / Not linked to an appointment'.
- For each test item, include:
  - Test name (embed hyperlink if available).
  - A 1–2 sentence result summary based on relevant SOAP notes lab result/imaging notes and result pdf (when notes is not available).
  - An inline citation link only when the summary depends on SOAP, lab, imaging, or attachment content (one link maximum per bullet).

---

## 4. Key Alerts
- From appointment-associated SOAP notes, list drug reactions and behavioral considerations for handling.
- Do not surface other findings.
- List alerts from the patient entity in the previous client tool call.

---

## 5. Current Medications
- Add description: 'Rx prescribed during the last 12 months:'
- List prescriptions prescribed within 12 months from today and **not** in a draft or canceled state.
- Include: full name, prescribed-on date, number of refills, refills remaining, last fill date, and instructions.
- Do not list Rx numbers.
- Sort by most recent first.

---

## 6. Other Medications
- Add description: 'Rx prescribed over 12 months ago and any vet diet, supplements, therapeutic injections, nutraceuticals mentioned in the patient records:'
- List prescriptions not mentioned in Current Medications, plus any veterinary diet, supplements, therapeutic injections and nutraceuticals.
- Show only the full name for each item as a bullet.

---

## 7. Pending Items

### Reminders:
- Only include reminders that meet one of these criteria:
  - State is overdue, OR
  - State is open AND due status is due_soon.
- Strict Suppression (Zero Tolerance):
  - If a reminder due status is due_later, it must be completely excluded.
- Format each reminder item as:
  - **Bolded title**
  - Followed by 1–2 concise sentences of clinical detail.

### Follow-ups:
- Display results generated in Step 3 only.
- Format each follow-up item as:
  - **Bolded title**
  - Followed by 1–2 concise sentences of clinical detail.

---

## Final Response Validation (Do before answering)
- Confirm sections '1' through '7' exist in order.
- Confirm no extra sections exist (including "Appointment" or "Summary").
- Confirm Section 3 lists diagnostic indicators as bullets grouped by appointment (and includes 'Other / Not linked to an appointment' when needed).
- Confirm inline citations are clickable Markdown links and limited to one per bullet.
- Confirm Strict Suppression rules were applied for reminders and follow-ups.
- If a required section has no data, include 'None.' as a single plain line (not a bullet).
-->`
